import { useMemo, useState } from 'react';
import { getSellerType, INTERNATIONAL_SHIPPING_MESSAGES } from '../constants';

/**
 * Custom hook that encapsulates all business logic for InternationalShipping
 * @param {Object} props - Component props
 * @param {Object} props.product - Product object
 * @param {Object} props.appCtx - Application context object
 * @returns {Object} - Processed data and handlers
 */
export const useInternationalShipping = ({ product, appCtx } = {}) => {
  const [intBuyToggle, setIntBuyToggle] = useState(false);
  const { internationalShipping: { applicable = false } = {}, sellerInfo: { sellerId } = {} } = product || {};

  const { regionCode, deviceType } = appCtx || {};

  // Determine seller type and get appropriate message
  const sellerType = getSellerType(sellerId, applicable);
  const internationalDeliveryText = useMemo(() => {
    return INTERNATIONAL_SHIPPING_MESSAGES[sellerType];
  }, [sellerType]);

  const modalProps = useMemo(() => {
    const baseProps = {
      stopScroll: true,
      customClassBody: 'auto',
      withPadding: false,
      deviceType,
    };

    const desktopConfig =
      deviceType === 'desktop'
        ? {
            closeBtnStyles: {
              top: '22px',
              right: '25px',
              fontSize: '11px',
            },
            options: {
              width: 500,
              height: 530,
              disableOutsideClick: false,
            },
          }
        : {};

    const mobileConfig =
      deviceType === 'mobile'
        ? {
            fullScreen: true,
            fullWidth: true,
            options: {
              overflow: 'hidden',
              disableOutsideClick: true,
              fullWidth: true,
            },
            closeBtnStyles: {
              top: '16px',
              right: '16px',
              fontSize: '15px',
            },
          }
        : {};

    return {
      ...baseProps,
      ...desktopConfig,
      ...mobileConfig,
    };
  }, [deviceType]);

  const handleToggle = () => {
    setIntBuyToggle((prevState) => !prevState);
  };

  const handleModalVisibility = (visible) => {
    setIntBuyToggle(visible);
  };

  const toggleState = useMemo(
    () => ({
      visible: intBuyToggle,
      setVisible: handleModalVisibility,
    }),
    [intBuyToggle]
  );

  const computedValues = useMemo(
    () => ({
      shouldRender: Boolean(applicable && appCtx),
      isModalOpen: intBuyToggle,
    }),
    [appCtx, intBuyToggle, applicable]
  );

  return {
    // State values
    intBuyToggle,

    // Processed data
    internationalDeliveryText,
    regionCode,
    deviceType,
    modalProps,
    toggleState,
    sellerType,
    sellerId,

    // Event handlers
    handleToggle,
    handleModalVisibility,

    // Computed values
    ...computedValues,
  };
};
